import React, { useState, useEffect } from "react";
import "../../../styles/PlantCode.css";
import axiosInstance from "../../../axiosInstance";
import { toast } from "react-toastify";
import Select from "react-select";
import { X } from "lucide-react";

const divisionOptions = [
    { label: "SCS", value: "scs" },
    { label: "FF", value: "ff" },
];

const PlantCodeCreation = ({ plantCodeId, onClose }) => {

    const isEditMode = !!plantCodeId;

    const [code, setCode] = useState("");
    const [description, setDescription] = useState("");
    const [divisionType, setDivisionType] = useState([]);
    const [loading, setLoading] = useState(false);

    const [errors, setErrors] = useState({
        code: "",
        divisionType: ""
    });

    useEffect(() => {
        if (plantCodeId) {
            fetchPlantCode();
        }
    }, [plantCodeId]);

    const fetchPlantCode = async () => {
        try {
            const response = await axiosInstance.get(`/plant_code/edit/${plantCodeId}`);
            const data = response.data.plant_code;
            setCode(data.code);
            setDescription(data.description);

            if (data.division_type) {
                const mappedDivision = data.division_type.map((item) => ({
                    label: item.toUpperCase(),
                    value: item,
                }));

                setDivisionType(mappedDivision);
            }

        } catch (error) {
            toast.error("Failed to fetch plant code");
            console.error(error);
        }
    };

    const handleClear = () => {
        setCode("");
        setDescription("");
        setDivisionType([]);

        setErrors({
            code: "",
            divisionType: ""
        });
    };

    const handleSubmit = async () => {
        const newErrors = {
            code: "",
            divisionType: ""
        };

        if (!code.trim()) {
            newErrors.code = "Code is required";
        }

        if (divisionType.length === 0) {
            newErrors.divisionType = "Division Type is required";
        }
        setErrors(newErrors);
        if (newErrors.code || newErrors.divisionType) {
            return;
        }

        const payload = {
            code,
            description,
            division_type: divisionType.map((item) => item.value),
        };

        try {

            setLoading(true);
            if (isEditMode) {
                await axiosInstance.put(`/plant_code/update`, {
                    id: plantCodeId,
                    ...payload
                });
                toast.success("Plant Code updated successfully");
            } else {
                await axiosInstance.post(`/plant_code/save`, payload);
                toast.success("Plant Code created successfully");
            }
            onClose();

        } catch (error) {
            const backendMessage =
                error?.response?.data?.message || "Failed to fetch Plant Codes";
            toast.error(backendMessage);
            console.error("Error fetching plant codes:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: "40px",
            borderRadius: "12px",
            fontSize: "13px",
            width: "100%",
            borderColor: state.isFocused ? "#454CF2" : "#D0D6DF",
            boxShadow: state.isFocused ? "0 0 0 1px #454CF2" : "none",
            "&:hover": { borderColor: "#454CF2" },
        }),
        container: (base) => ({ ...base, width: "100%" }),
        menu: (base) => ({ ...base, fontSize: "12px" }),
        menuList: (base) => ({
            ...base,
            maxHeight: "250px",
            overflowY: "auto"
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: '#F0F4F8',
            borderRadius: '4px',
            padding: '2px 6px',
            margin: '2px',
            maxWidth: '120px',
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: '#19324C',
            fontSize: '12px',
            fontWeight: 500
        }),
        valueContainer: (base) => ({
            ...base,
            maxHeight: "35px",
            height: "40px",
            overflowY: "auto",
            overflowX: "hidden",
            flexWrap: "wrap",
            alignItems: "center",
            paddingTop: "4px",
            paddingBottom: "4px",
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: '#19324C',
            ':hover': {
                backgroundColor: 'transparent',
                color: '#ff0000'
            }
        })
    };

    return (
        <div className="plantcode-creation-popup-overlay">
            <div className="plantcode-creation-popup-container">
                {/* Header */}
                <div className="plantcode-creation-popup-header">
                    <h2>{isEditMode ? "Edit Plant Code" : "Create Plant Code"}</h2>

                    <div
                        className="plantcode-close-button" onClick={handleClose}>
                        <X size={16} />
                    </div>
                </div>

                <div className="plantcode-creation-popup-body">
                    <div className="plantcode-creation-form-group">
                        <label>
                            Code <span style={{ color: "red" }}>*</span>
                        </label>
                        <input
                            type="text"
                            placeholder="Enter code..."
                            value={code}
                            onChange={(e) => {
                                setCode(e.target.value);
                                if (errors.code) {
                                    setErrors((prev) => ({
                                        ...prev,
                                        code: ""
                                    }));
                                }
                            }}
                            style={{ margin: '0px' }}
                        />
                        {errors.code && (
                            <p className="plantcode-error-text">{errors.code}</p>
                        )}
                    </div>

                    {/* Description */}
                    <div className="plantcode-creation-form-group">
                        <label>
                            Description
                        </label>

                        <input
                            type="text"
                            placeholder="Enter description..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            style={{ margin: '0px' }}
                        />
                    </div>

                    {/* Division Type */}
                    <div className="plantcode-creation-form-group">
                        <label>
                            Division Type <span style={{ color: "red" }}>*</span>
                        </label>

                        <Select
                            options={divisionOptions}
                            isMulti
                            value={divisionType}
                            onChange={(selected) => {
                                setDivisionType(selected || []);
                                if (errors.divisionType) {
                                    setErrors((prev) => ({
                                        ...prev,
                                        divisionType: ""
                                    }));
                                }
                            }}
                            placeholder="Select Division Type"
                            closeMenuOnSelect={false}
                            className="plantcode-creation-select-division-type"
                            styles={customSelectStyles}
                        />
                        {errors.divisionType && (
                            <p className="plantcode-error-text">{errors.divisionType}</p>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="plantcode-creation-popup-footer">
                    <button
                        className="plantcode-clear-button"
                        onClick={handleClear}
                    >
                        Clear
                    </button>
                    <button
                        className="plantcode-submit-button"
                        onClick={handleSubmit}
                        disabled={loading}>
                        {loading ? "Saving..." : isEditMode ? "Update" : "Submit"} </button>
                </div>
            </div>
        </div>
    );
};

export default PlantCodeCreation;